<template>
  <section class="section-block">
    <SectionTitle :title="displayText.title" :btn="displayText.btn.create" @edit="onCreate" />
    <div>
      <BaseElForm
        :model="formData"
        label-position="left"
        label-width="250px"
      >
        <BaseElFormItem :label="displayText.enablePaymentIcon.label" prop="enablePaymentIcon">
          <BaseElSwitch
            v-model="formData.enablePaymentIcon"
            :active-text="displayText.switch.activeText"
            :inactive-text="displayText.switch.inactiveText"
            @change="enableChange"
          />
        </BaseElFormItem>
      </BaseElForm>
      <BaseTable :data="displayData">
        <template #empty> {{ displayText.table.empty }} </template>
        <BaseElTableColumn :label="displayText.table.name" prop="name" width="200" align="center" />
        <BaseElTableColumn :label="displayText.table.enable" prop="displayEnable" align="center">
          <template slot-scope="scope">
            <Tag :type="scope.row.enable ? 'action' : 'danger'">
              {{ scope.row.displayEnable }}
            </Tag>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :label="displayText.table.operation" fixed="right" align="center" width="200">
          <template slot-scope="scope">
            <TableEditBtnGroup
              :deleteDisabled="disableDelete"
              @edit="onEditPayment(scope.row)"
              @delete="onDeletePayment(scope.row)"
            />
          </template>
        </BaseElTableColumn>
      </BaseTable>
    </div>

    <EditPaymentModal
      v-if="modal.edit"
      :editType="modalType"
      :configData="payments"
      :paymentOptions="paymentOptions"
      :availablesPayments="availablesPayments"
      :select-payment="selectPayment"
      :updateApi="updateApi"
      :createApi="createApi"
      @close="modal.edit = false"
      @refresh="$emit('refresh')"
    />

    <DeleteDialog
      v-if="modal.delete"
      :title="displayText.dialog.title"
      :content="displayText.dialog.content"
      :loading="loading.delete"
      hideCancel
      width="40%"
      @close="modal.delete = false"
      @delete="deletePayment"
    />
  </section>
</template>

<script>
import { defineComponent, reactive, computed, ref, onMounted, watch } from 'vue'
import SectionTitle from '@/components/Title/SectionTitle.vue'
import EditPaymentModal from './EditPaymentModal.vue'
import DeleteDialog from '@/components/Dialog/DeleteDialog.vue'
import { map, get, find, filter } from 'lodash'
import { useShop } from '@/use/shop'
import { useExternalWallet } from '@/use/useExternalWallet'
import notifyMessage from '@/config/notifyMessage'
import { paymentOptionsConfig } from '@/config/payment'
import Tag from '@/components/Tag/Tag.vue'
import { UpdateReservationParameter } from '@/api/reservation'

export default defineComponent({
  name: 'PaymentSettingBlock',
  components: { SectionTitle, EditPaymentModal, DeleteDialog, Tag },
  props: {
    deleteLast: { type: Boolean, default: true },
    reservation: { type: Object, default: () => ({}) },
    payments: { type: Array, default: () => [] },
    updateApi: { type: Function, default: () => {} },
    createApi: { type: Function, default: () => {} },
    deleteApi: { type: Function, default: () => {} },
    availablesPayments: { type: Array, default: () => [] },
  },
  setup (props, { emit }) {
    const { shopId } = useShop()
    const modalType = ref('create')
    const selectPayment = ref({})
    const { externalWalletName, getExternalWalletConfig } = useExternalWallet()
    const loading = reactive({ delete: false, enable: false })
    const modal = reactive({
      edit: false,
      delete: false,
    })
    const formData = reactive({
      enablePaymentIcon: false,
    })
    const disableDelete = computed(() => {
      return !props.deleteLast && props.payments.length === 1
    })

    const paymentOptions = computed(() => {
      return filter(
        map(filter(paymentOptionsConfig, (i) => props.availablesPayments.includes(i.value)), (item) => ({
          value: item.value,
          label: item.value === 'externalWallet' ? externalWalletName.value : item.name,
        })),
        (option) => !find(props.payments, { type: option.value }),
      )
    })
    const displayText = computed(() => {
      const data = {
        title: '付款方式',
        btn: {
          create: '新增',
        },
        enablePaymentIcon: {
          label: '是否於顧客端顯示付款方式圖示',
        },
        switch: {
          activeText: '顯示',
          inactiveText: '隱藏',
        },
        table: {
          empty: '尚未有付款方式紀錄，請先新增',
          name: '名稱',
          enable: '啟用',
          operation: '操作',
        },
        dialog: {
          title: '提醒',
          content: '刪除後將無法復原，確定要刪除？',
        },
      }
      return data
    })
    const displayData = computed(() => {
      return map(props.payments, (item) => {
        return {
          name: item.type === 'externalWallet' ? externalWalletName.value : get(paymentOptionsConfig, `${item.type}.name`, '-'),
          enable: item.enable,
          displayEnable: item.enable ? '是' : '否',
          type: item.type,
          id: item.id,
        }
      })
    })

    const onCreate = () => {
      selectPayment.value = {}
      modal.edit = true
      modalType.value = 'create'
    }

    const onEditPayment = (payment) => {
      modal.edit = true
      modalType.value = 'edit'
      selectPayment.value = payment
    }
    const onDeletePayment = (payment) => {
      modal.delete = true
      selectPayment.value = payment
    }

    const deletePayment = async () => {
      if (loading.delete) return
      loading.delete = true
      const [, errMsg] = await props.deleteApi({
        shopId: shopId.value,
        id: selectPayment.value.id,
      })
      loading.delete = false
      if (errMsg) {
        window.$message.error(errMsg)
        return
      }
      window.$message.success(notifyMessage.deleteSuccess)
      emit('refresh')
      modal.delete = false
    }
    const enableChange = async () => {
      if (loading.enable) return
      loading.enable = true
      await updateReservationParameter()
      loading.enable = false
    }
    const updateReservationParameter = async () => {
      const [, err] = await UpdateReservationParameter({
        shopId: shopId.value,
        enablePaymentIcon: formData.enablePaymentIcon,
      })
      if (err) {
        window.$message.error(err)
        return
      }
      window.$message.success('更新成功!')
      emit('refresh')
    }
    watch(
      () => props.reservation.enablePaymentIcon,
      (val) => {
        formData.enablePaymentIcon = val
      },
      { immediate: true },
    )
    onMounted(async () => {
      await getExternalWalletConfig()
      formData.enablePaymentIcon = props.reservation.enablePaymentIcon
    })
    return {
      onCreate,
      modal,
      paymentOptions,
      displayData,
      modalType,
      onEditPayment,
      onDeletePayment,
      selectPayment,
      deletePayment,
      loading,
      disableDelete,
      formData,
      enableChange,
      displayText,
    }
  },
})
</script>

<style lang="postcss" scoped></style>
